import React, { useState, useRef, useEffect } from 'react';
import { ISeparatorBlock } from './types';
import Bars3 from '../../icons/Bars3';
import Bars3BottomLeft from '../../icons/Bars3BottomLeft';
import Bars3BottomRight from '../../icons/Bars3BottomRight';

interface SeparatorWidgetProps {
  block?: ISeparatorBlock;
  onChange?: (newBlock: ISeparatorBlock) => void;
  mode?: 'editor' | 'reader';
}

const SeparatorWidget: React.FC<SeparatorWidgetProps> = ({ block, onChange, mode = 'reader' }) => {
  const [showStyleMenu, setShowStyleMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const currentStyle = block?.style || 'solid';
  const currentWidth = block?.width || 100;
  const currentHeight = block?.height || 2;
  const currentVerticalPadding = block?.verticalPadding || 16;
  const currentAlignment = block?.alignment || 'center';

  // Fermer le menu si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowStyleMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateBlock = (updates: Partial<ISeparatorBlock>) => {
    if (onChange && block) {
      onChange({ ...block, ...updates });
    }
  };

  const getBorderStyle = () => {
    switch (currentStyle) {
      case 'dashed': return 'dashed';
      case 'dotted': return 'dotted';
      default: return 'solid';
    }
  };

  const getAlignmentClass = () => {
    switch (currentAlignment) {
      case 'left': return 'justify-start';
      case 'right': return 'justify-end';
      default: return 'justify-center'; // Center by default
    }
  };

  return (
    <div
      className={`relative select-none group flex ${getAlignmentClass()} border border-transparent ${mode === 'editor' ? 'hover:border-[#f0f0f0]' : ''} transition-colors duration-200`}
      style={{ paddingTop: `${currentVerticalPadding}px`, paddingBottom: `${currentVerticalPadding}px` }}
    >
      {mode === 'editor' && (
        <div className="absolute top-0 right-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity" style={{ top: '-12px' }}>
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowStyleMenu(!showStyleMenu)}
              className="px-3 py-0.5 rounded-full border border-gray-300 bg-white hover:bg-gray-50 dark:bg-mcdm-third-400 dark:border-mcdm-third-300 dark:hover:bg-mcdm-third-300 text-xs font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 transition"
            >
              Style
            </button>

            {showStyleMenu && (
              <div className="absolute top-full right-0 mt-1 w-64 bg-white dark:bg-mcdm-third-400 border border-gray-200 dark:border-mcdm-third-500 rounded-lg shadow-lg z-20 p-3 flex flex-col gap-4">

                {/* Style du trait */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 block uppercase">Type</label>
                  <div className="flex gap-1">
                    {['solid', 'dashed', 'dotted'].map((style) => (
                      <button
                        key={style}
                        onClick={() => updateBlock({ style: style as any })}
                        className={`flex-1 py-1 text-xs border rounded ${currentStyle === style
                          ? 'bg-mcdm-secondary-50 border-mcdm-secondary-200 text-mcdm-secondary-700 dark:bg-mcdm-secondary-900/30 dark:border-mcdm-secondary-700 dark:text-mcdm-secondary-300'
                          : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-mcdm-third-300 text-gray-600 dark:text-gray-300'}`}
                      >
                        {style === 'solid' ? 'Plein' : style === 'dashed' ? 'Tirets' : 'Points'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Dimensions (Largeur, Hauteur, Épaisseur) */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 block uppercase">Dimensions</label>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 dark:text-gray-300 w-16">Largeur</span>
                      <input
                        type="range"
                        min="10"
                        max="100"
                        step="10"
                        value={currentWidth}
                        onChange={(e) => updateBlock({ width: parseInt(e.target.value) })}
                        className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-mcdm-secondary-500"
                      />
                      <span className="text-xs text-gray-500 w-8 text-right">{currentWidth}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 dark:text-gray-300 w-16">Hauteur</span>
                      <input
                        type="range"
                        min="4"
                        max="64"
                        step="4"
                        value={currentVerticalPadding}
                        onChange={(e) => updateBlock({ verticalPadding: parseInt(e.target.value) })}
                        className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-mcdm-secondary-500"
                      />
                      <span className="text-xs text-gray-500 w-8 text-right">{currentVerticalPadding * 2}px</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 dark:text-gray-300 w-16">Épaisseur</span>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={currentHeight}
                        onChange={(e) => updateBlock({ height: parseInt(e.target.value) })}
                        className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-mcdm-secondary-500"
                      />
                      <span className="text-xs text-gray-500 w-8 text-right">{currentHeight}px</span>
                    </div>
                  </div>
                </div>

                {/* Alignement */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 block uppercase">Alignement</label>
                  <div className="flex bg-gray-100 dark:bg-mcdm-third-500 rounded p-1 gap-1">
                    <button
                      onClick={() => updateBlock({ alignment: 'left' })}
                      className={`flex-1 py-1 rounded flex items-center justify-center ${currentAlignment === 'left' ? 'bg-white shadow text-black dark:bg-mcdm-third-300 dark:text-mcdm-primary-100' : 'text-gray-500 hover:text-gray-700 dark:text-mcdm-primary-200'}`}
                      title="Gauche"
                    >
                      <Bars3BottomLeft width="16" height="16" />
                    </button>
                    <button
                      onClick={() => updateBlock({ alignment: 'center' })}
                      className={`flex-1 py-1 rounded flex items-center justify-center ${currentAlignment === 'center' ? 'bg-white shadow text-black dark:bg-mcdm-third-300 dark:text-mcdm-primary-100' : 'text-gray-500 hover:text-gray-700 dark:text-mcdm-primary-200'}`}
                      title="Centre"
                    >
                      <Bars3 width="16" height="16" />
                    </button>
                    <button
                      onClick={() => updateBlock({ alignment: 'right' })}
                      className={`flex-1 py-1 rounded flex items-center justify-center ${currentAlignment === 'right' ? 'bg-white shadow text-black dark:bg-mcdm-third-300 dark:text-mcdm-primary-100' : 'text-gray-500 hover:text-gray-700 dark:text-mcdm-primary-200'}`}
                      title="Droite"
                    >
                      <Bars3BottomRight width="16" height="16" />
                    </button>
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>
      )}

      <hr
        style={{
          width: `${currentWidth}%`,
          borderTopWidth: `${currentHeight}px`,
          borderStyle: getBorderStyle(),
        }}
        className="border-gray-200 dark:border-gray-600 transition-all duration-200"
      />
    </div>
  );
};


export default SeparatorWidget;
